import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const ProductImageCarousel = ({ customImages = null }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const defaultImages = [
    {
      src: '/images/modules/module1.webp',
      alt: 'BlueSpark Pro Tuning Module - Front View',
      caption: 'Pro Tuning Module'
    },
    {
      src: '/images/modules/module2.webp',
      alt: 'BlueSpark Pro Tuning Module - Connection View',
      caption: 'Connection Ports'
    },
    {
      src: '/images/modules/module3.webp',
      alt: 'BlueSpark Pro Tuning Module - Internal Circuit',
      caption: 'Internal Circuit Board'
    },
    {
      src: '/images/modules/module4.webp',
      alt: 'BlueSpark Bluetooth Control App',
      caption: 'Bluetooth App Control'
    }
  ];

  // Normalise input — accept either an array of plain URL strings (from the DB)
  // or an array of {src, alt, caption} objects (legacy/mockdata shape).
  const normalised = (customImages || [])
    .map((it, i) => {
      if (!it) return null;
      if (typeof it === 'string') {
        return { src: it, alt: `Product image ${i + 1}`, caption: '' };
      }
      if (typeof it === 'object' && it.src) {
        return { src: it.src, alt: it.alt || `Product image ${i + 1}`, caption: it.caption || '' };
      }
      return null;
    })
    .filter(Boolean);

  const images = normalised.length > 0 ? normalised : defaultImages;

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative w-full">
      {/* Main Image Display */}
      <div className="relative bg-white rounded-lg overflow-hidden aspect-square border border-gray-200">
        <img
          src={images[currentIndex].src}
          alt={images[currentIndex].alt}
          width={800}
          height={800}
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-contain p-8"
        />
        
        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-gray-800 hover:bg-black text-white p-2 rounded-full shadow-lg transition-all z-10"
          aria-label="Previous image"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        
        <button
          onClick={goToNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-gray-800 hover:bg-black text-white p-2 rounded-full shadow-lg transition-all z-10"
          aria-label="Next image"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Image Caption */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900/80 to-transparent p-4">
          <p className="text-white text-center font-medium">
            {images[currentIndex].caption}
          </p>
        </div>
      </div>

      {/* Thumbnail Navigation */}
      <div className="flex gap-2 mt-4 justify-center">
        {images.map((image, index) => (
          <button
            key={`${image}-${index}`}
            onClick={() => goToSlide(index)}
            className={`relative overflow-hidden rounded border-2 transition-all ${
              index === currentIndex 
                ? 'border-blue-600 ring-2 ring-blue-200' 
                : 'border-gray-300 hover:border-blue-400'
            }`}
            style={{ width: '80px', height: '80px' }}
          >
            <img
              src={image.src}
              alt={image.alt || `Thumbnail ${index + 1}`}
              width={80}
              height={80}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>

      {/* Slide Counter */}
      <div className="text-center mt-3 text-sm text-gray-600">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
};

export default ProductImageCarousel;
